// Shared layout for every clinician-side auth screen (login, MFA setup/entry,
// password reset). Extracting this guarantees the backdrop + card + logo cannot
// drift between screens. Backdrop glow, card radius and logo sizing are all
// theme-driven (see authGlow.js and theme.auth in Dashboard.jsx createTheme).
//
// Logo: the admin/clinician auth screens intentionally stay TrakGene-branded
// (the /system-params/default-theme endpoint strips logoURL by design), so the
// default is the bundled asset. A caller can still pass a different logo.
import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import defaultLogo from 'src/assets/images/trakgeneLogo.png';
import { authGlowSx } from './authGlow';
import useSystemDefaultTheme from 'src/hooks/useSystemDefaultTheme';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const FALLBACK = {
  cardRadius: 16,
  cardMaxWidth: 460,
  narrowMaxWidth: 420,
  logoMaxHeight: 56
};

export default function AuthLayout({
  children,
  logoSrc = defaultLogo,
  logoAlt = 'Trakgene Logo',
  variant = 'login',
  topRight = null
}) {
  const t = useAutoTranslation();
  // Anonymous screens take the System Admin's appearance, not MUI defaults.
  useSystemDefaultTheme();
  const theme = useTheme();
  const auth = { ...FALLBACK, ...(theme.auth || {}) };
  const isDark = theme.palette?.mode === 'dark';
  const maxWidth =
    variant === 'narrow' ? auth.narrowMaxWidth : auth.cardMaxWidth;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        p: 2,
        overflowX: 'hidden',
        transition: 'background-color 0.6s ease',
        ...authGlowSx(theme)
      }}
    >
      {topRight && (
        <Box
          sx={{ position: 'absolute', top: 16, right: 24, textAlign: 'right' }}
        >
          {topRight}
        </Box>
      )}
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth,
          borderRadius: `${auth.cardRadius}px`,
          px: { xs: 3, sm: 5, md: 6 },
          py: { xs: 4, sm: 5 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          boxShadow: isDark
            ? '0 24px 60px -20px rgba(0,0,0,0.7)'
            : '0 24px 60px -24px rgba(15,23,42,0.25)'
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt={t(logoAlt)}
          sx={{
            maxWidth: '75%',
            maxHeight: auth.logoMaxHeight,
            height: 'auto',
            objectFit: 'contain'
          }}
        />
        <Box sx={{ width: '100%' }}>{children}</Box>
      </Paper>
    </Box>
  );
}
